/**
 * FanLoginPage — Standalone fan login page with OTP flow.
 * Supports ?redirect=<path> to redirect after successful authentication.
 * Used when a fan clicks "Already a Fan? Login here." on the artist profile.
 */
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ArrowRight, Shield, ArrowLeft } from 'lucide-react';
import { requestFanOtp, verifyFanOtp } from '../services/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

export default function FanLoginPage() {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [codeChanged, setCodeChanged] = useState(false);

  // Get redirect URL from query params; only allow same-origin relative paths.
  const redirectParam = new URLSearchParams(window.location.search).get('redirect');
  const redirectUrl = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
    ? redirectParam
    : null;

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const res = await requestFanOtp(email.trim());
      const data = await res.json() as { success?: boolean; message?: string; debugCode?: string; error?: string };
      if (res.ok) {
        if (data.debugCode) {
          setMessage(`A verification code has been sent to your email. Dev code: ${data.debugCode}`);
        } else {
          setMessage(data.message || 'A verification code has been sent to your email.');
        }
        setStep('code');
      } else {
        setError(data.error || 'Failed to send code. Make sure you have a fan account.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const autoSubmitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (autoSubmitTimerRef.current) {
      clearTimeout(autoSubmitTimerRef.current);
      autoSubmitTimerRef.current = null;
    }
    if (code.length === 6 && step === 'code' && !loading && codeChanged) {
      autoSubmitTimerRef.current = setTimeout(() => {
        handleVerifyOtp();
      }, 2000);
    }
    return () => {
      if (autoSubmitTimerRef.current) {
        clearTimeout(autoSubmitTimerRef.current);
      }
    };
  }, [code, step, loading, codeChanged]);

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    setLoading(true);
    setCodeChanged(false);

    try {
      const res = await verifyFanOtp(email.trim(), code.trim());
      const data = await res.json() as any;

      if (res.ok && data.success) {
        const userReference = typeof data?.user?.userReference === 'string' ? data.user.userReference : null;

        // If no meaningful redirect was provided, send the fan to their collection.
        const fallbackUrl = userReference ? `/my-collection/${userReference}` : '/for-fans';
        const destination = redirectUrl && redirectUrl !== '/' ? redirectUrl : fallbackUrl;
        window.location.href = destination;
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4 py-16">
      <div className="absolute inset-0 waveform-bg opacity-20" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-electric-cyan/5 rounded-full blur-[120px]" />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative z-10 w-full max-w-md"
      >
        {/* Back button */}
        <motion.div variants={fadeInUp} className="mb-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-1.5 text-sm text-text-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </motion.div>

        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg tracking-tight text-white/60">
              fans<span className="text-electric-cyan">.audio</span>
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Fan Login</h1>
          <p className="text-sm text-text-secondary">
            Sign in with the email address you used to join an artist's community.
          </p>
        </motion.div>

        {/* Card */}
        <motion.div
          variants={fadeInUp}
          className="bg-[#1A1A1A]/80 border border-white/10 rounded-2xl p-8 backdrop-blur-xl"
        >
          {step === 'email' && (
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div>
                <label htmlFor="fan-email" className="block text-xs font-mono text-text-muted uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <input
                  id="fan-email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(null); }}
                  placeholder="your@email.com"
                  disabled={loading}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan focus:ring-2 focus:ring-electric-cyan/40 transition-all duration-300 disabled:opacity-50"
                  autoFocus
                />
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-red-400"
                >
                  {error}
                </motion.p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-bold text-sm text-[#0D0D0D] transition-all disabled:opacity-70 cursor-pointer flex items-center justify-center gap-2"
                style={{
                  background: 'rgba(0, 245, 255, 0.9)',
                  boxShadow: '0 0 20px rgba(0, 245, 255, 0.3)',
                }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Send Code
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              {message && (
                <p className="text-xs text-green-400 text-center">{message}</p>
              )}

              <div>
                <label htmlFor="fan-code" className="block text-xs font-mono text-text-muted uppercase tracking-wider mb-2">
                  Verification Code
                </label>
                <input
                  id="fan-code"
                  type="text"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(val);
                    setCodeChanged(true);
                    setError(null);
                  }}
                  placeholder="000000"
                  disabled={loading}
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-text-muted text-sm text-center tracking-[0.5em] font-mono focus:outline-none focus:border-electric-cyan focus:ring-2 focus:ring-electric-cyan/40 transition-all duration-300 disabled:opacity-50"
                  autoFocus
                />
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-red-400"
                >
                  {error}
                </motion.p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-bold text-sm text-[#0D0D0D] transition-all disabled:opacity-70 cursor-pointer flex items-center justify-center gap-2"
                style={{
                  background: 'rgba(0, 245, 255, 0.9)',
                  boxShadow: '0 0 20px rgba(0, 245, 255, 0.3)',
                }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Verify & Login
                    <Shield className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setMessage(null); setError(null); }}
                className="w-full text-center text-xs text-text-muted hover:text-white transition-colors cursor-pointer"
              >
                Use a different email
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}
